// src/lib/device.ts -- device sensing (2026-11-04).
//
// Classifies the browser as iPhone / iPad / Android / desktop once at startup
// and publishes the answer as `data-device` on <html> (plus `data-touch`
// yes|no). CSS keys off those attributes: on desktop every page and every
// internally scrolling panel gets a classic, always-visible scrollbar (see
// index.css); touch devices keep their native overlay bars.
//
// iPadOS 13+ reports a Macintosh user agent; the touch-point count is what
// separates it from a real Mac.

export type DeviceKind = "iphone" | "ipad" | "android" | "desktop";

export function detectDevice(): DeviceKind {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  const touchPoints = navigator.maxTouchPoints || 0;
  if (/iPhone|iPod/i.test(ua)) return "iphone";
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && touchPoints > 1)) return "ipad";
  if (/Android/i.test(ua)) return "android";
  // Windows tablets / Surface in touch mode still have a mouse-class pointer;
  // a coarse primary pointer with no fine one is the only desktop-UA case we
  // treat as touch.
  const coarseOnly =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches &&
    !window.matchMedia("(any-pointer: fine)").matches;
  if (coarseOnly && touchPoints > 1) return "android";
  return "desktop";
}

export function isTouchDevice(kind: DeviceKind = detectDevice()): boolean {
  return kind !== "desktop";
}

/** Publish the device class on <html>. Idempotent. */
export function publishDeviceClass(): DeviceKind {
  const kind = detectDevice();
  if (typeof document !== "undefined") {
    document.documentElement.dataset.device = kind;
    document.documentElement.dataset.touch = kind === "desktop" ? "no" : "yes";
  }
  return kind;
}
